// Pago de Clientes › Consultas › Cartola por vendedor (como la cartola general de Random, Rodrigo
// 2026-10-29): la cuenta corriente de los clientes que su FICHA asigna al vendedor, para ver quién está
// en mora y quién dentro del plazo. El estado lo calcula el servidor con la regla del control de crédito
// (vencido más allá de los días de morosidad permitida de la ficha = en mora).
import { useCallback, useMemo, useState } from 'react';
import { useVendedores } from '../api/consultas';
import { PanelError } from '../comun/Estados';
import { Grilla, type Columna } from '../comun/Grilla';
import { NOMBRE_CONDICION, pesos } from '../comun/formato';
import { useSesion } from '../sesion/SesionContexto';
import {
  useCartolaPorVendedor,
  type CuentaDeCartera,
  type EstadoCuenta,
  type TotalVendedor,
  type Tramo,
} from './datos';
import { ImprimirCartolaVendedor } from './Impresos';

export const NOMBRE_ESTADO_CUENTA: Record<EstadoCuenta, string> = {
  EN_MORA: 'En mora',
  VENCIDO_TOLERADO: 'Vencido con tolerancia',
  AL_DIA: 'Al día',
  SIN_DEUDA: 'Sin deuda',
};
const TONO_ESTADO: Record<EstadoCuenta, string> = {
  EN_MORA: 'tono-malo',
  VENCIDO_TOLERADO: 'tono-aviso',
  AL_DIA: 'tono-ok',
  SIN_DEUDA: 'tono-info',
};
const TRAMOS: Tramo[] = ['corriente', '1-30', '31-60', '61-90', '91+'];

/** «Crédito 30 días», «Contado». */
export const condicionDe = (c: Pick<CuentaDeCartera, 'condicionPago' | 'plazoDias'>) => {
  const base = c.condicionPago ? (NOMBRE_CONDICION[c.condicionPago] ?? c.condicionPago) : '—';
  return c.plazoDias > 0 ? `${base} ${c.plazoDias} días` : base;
};

/**
 * Totales por vendedor de las filas que se ven (con el filtro de estado o la búsqueda aplicados), con
 * la cartera (clientes asignados en la ficha) que informa el servidor.
 */
export function totalesVisibles(
  vendedores: readonly TotalVendedor[],
  clientes: readonly CuentaDeCartera[],
): TotalVendedor[] {
  return vendedores.map((v) => {
    const suyos = clientes.filter((c) => c.vendedorId === v.vendedorId);
    const suma = (f: (c: CuentaDeCartera) => string) =>
      String(suyos.reduce((s, c) => s + BigInt(f(c)), 0n));
    const porEstado = { EN_MORA: 0, VENCIDO_TOLERADO: 0, AL_DIA: 0, SIN_DEUDA: 0 };
    for (const c of suyos) porEstado[c.estado]++;
    return {
      ...v,
      clientes: suyos.length,
      porEstado,
      saldo: suma((c) => c.saldo),
      vencido: suma((c) => c.vencido),
      saldoAFavor: suma((c) => c.saldoAFavor),
      neto: suma((c) => c.neto),
      tramos: Object.fromEntries(TRAMOS.map((t) => [t, suma((c) => c.tramos[t])])) as Record<
        Tramo,
        string
      >,
    };
  });
}

const plata = (v: string) => (Number(v) ? pesos(v) : '');

export function CartolaVendedor({
  alAbrirCartola,
}: {
  alAbrirCartola: (clienteId: string, nombre: string) => void;
}) {
  const { empresa } = useSesion();
  const vendedores = useVendedores(empresa!.id);
  const [vendedorId, setVendedorId] = useState<string | null>(null);
  const [estado, setEstado] = useState<EstadoCuenta | ''>('');
  const [texto, setTexto] = useState('');
  const [sinDeuda, setSinDeuda] = useState(false);
  const [conDocumentos, setConDocumentos] = useState(true);
  const [imprimir, setImprimir] = useState(false);
  const terminar = useCallback(() => setImprimir(false), []);
  const q = useCartolaPorVendedor(empresa!.id, {
    vendedorId,
    incluirSinDeuda: !!vendedorId && sinDeuda,
  });

  const opciones = useMemo(
    () =>
      (vendedores.data ?? [])
        .filter((v) => v.activo)
        .sort((a, b) => a.codigo.localeCompare(b.codigo)),
    [vendedores.data],
  );
  const elegido = opciones.find((v) => v.id === vendedorId) ?? null;
  const filas = useMemo(() => {
    const t = texto.trim().toLowerCase();
    return (q.data?.clientes ?? []).filter(
      (c) =>
        (!estado || c.estado === estado) &&
        (!t || c.cliente.toLowerCase().includes(t) || (c.rut ?? '').includes(t)),
    );
  }, [q.data, estado, texto]);
  const totales = useMemo(() => totalesVisibles(q.data?.vendedores ?? [], filas), [q.data, filas]);
  const suma = (f: (c: CuentaDeCartera) => string) =>
    String(filas.reduce((s, c) => s + BigInt(f(c)), 0n));
  const cuantos = (e: EstadoCuenta) => filas.filter((c) => c.estado === e).length;
  const cartera = (q.data?.vendedores ?? []).reduce((s, v) => s + v.cartera, 0);
  const vencidoMora = String(
    filas.filter((c) => c.estado === 'EN_MORA').reduce((s, c) => s + BigInt(c.vencido), 0n),
  );

  const columnas: Columna<CuentaDeCartera>[] = [
    ...(vendedorId
      ? []
      : [
          {
            id: 'vendedor',
            titulo: 'Vend.',
            celda: (c: CuentaDeCartera) => <span className="mono">{c.vendedorCodigo ?? '—'}</span>,
            ancho: '4.5rem',
          },
        ]),
    {
      id: 'cliente',
      titulo: 'Cliente',
      celda: (c) => (
        <span className="nombre-cliente" title={c.cliente}>
          {c.cliente}
        </span>
      ),
    },
    {
      id: 'rut',
      titulo: 'RUT',
      celda: (c) => <span className="mono">{c.rut ?? ''}</span>,
      ancho: '8rem',
    },
    {
      id: 'estado',
      titulo: 'Estado',
      celda: (c) => (
        <>
          <span className={`insignia ${TONO_ESTADO[c.estado]}`}>
            {NOMBRE_ESTADO_CUENTA[c.estado]}
          </span>
          {c.bloqueado && <span className="insignia tono-malo">Bloqueado</span>}
        </>
      ),
      ancho: '9.5rem',
    },
    { id: 'condicion', titulo: 'Condición', celda: (c) => condicionDe(c), ancho: '8rem' },
    {
      id: 'docs',
      titulo: 'Docs',
      celda: (c) => c.documentos || '',
      alinear: 'der',
      ancho: '3rem',
    },
    {
      id: 'saldo',
      titulo: 'Saldo',
      celda: (c) => <b>{pesos(c.saldo)}</b>,
      alinear: 'der',
      ancho: '7rem',
    },
    {
      id: 'corriente',
      titulo: 'Por vencer',
      celda: (c) => plata(c.tramos.corriente),
      alinear: 'der',
      ancho: '6.5rem',
    },
    ...(['1-30', '31-60', '61-90', '91+'] as const).map((t) => ({
      id: t,
      titulo: t === '91+' ? '+90' : t,
      celda: (c: CuentaDeCartera) => (
        <span className={Number(c.tramos[t]) ? 'tono-malo-texto' : ''}>{plata(c.tramos[t])}</span>
      ),
      alinear: 'der' as const,
      ancho: '6rem',
    })),
    {
      id: 'dias',
      titulo: 'Días atr.',
      celda: (c) =>
        c.diasMaxVencido > 0 ? <b className="tono-malo-texto">{c.diasMaxVencido}</b> : '',
      alinear: 'der',
      ancho: '4.5rem',
    },
    {
      id: 'favor',
      titulo: 'A favor',
      celda: (c) => plata(c.saldoAFavor),
      alinear: 'der',
      ancho: '6rem',
    },
    { id: 'neto', titulo: 'Neto', celda: (c) => pesos(c.neto), alinear: 'der', ancho: '7rem' },
  ];

  return (
    <div className="pantalla consulta cartola-vendedor" data-pantalla="cartola-vendedor">
      <div className="barra-filtros">
        <label className="campo">
          <span className="etiqueta">Vendedor</span>
          <select
            aria-label="Vendedor"
            value={vendedorId ?? ''}
            onChange={(e) => setVendedorId(e.target.value || null)}
          >
            <option value="">Todos los vendedores</option>
            {opciones.map((v) => (
              <option key={v.id} value={v.id}>
                {v.codigo} · {v.nombre}
              </option>
            ))}
          </select>
        </label>
        <label className="campo">
          <span className="etiqueta">Estado</span>
          <select
            aria-label="Estado de la cuenta"
            value={estado}
            onChange={(e) => setEstado(e.target.value as EstadoCuenta | '')}
          >
            <option value="">Todos</option>
            <option value="EN_MORA">En mora</option>
            <option value="VENCIDO_TOLERADO">Vencido con tolerancia</option>
            <option value="AL_DIA">Al día</option>
            {vendedorId && sinDeuda && <option value="SIN_DEUDA">Sin deuda</option>}
          </select>
        </label>
        <label className="campo">
          <span className="etiqueta">Buscar</span>
          <input
            aria-label="Buscar cliente en la cartola"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
          />
        </label>
        <label className="casilla-filtro" title="Elige un vendedor para ver toda su cartera">
          <input
            type="checkbox"
            checked={!!vendedorId && sinDeuda}
            disabled={!vendedorId}
            onChange={(e) => setSinDeuda(e.target.checked)}
          />{' '}
          Incluir clientes sin deuda
        </label>
        <span className="espaciador" />
        <label className="casilla-filtro">
          <input
            type="checkbox"
            checked={conDocumentos}
            onChange={(e) => setConDocumentos(e.target.checked)}
          />{' '}
          Con documentos
        </label>
        <button type="button" disabled={!q.data} onClick={() => setImprimir(true)}>
          Imprimir cartola
        </button>
      </div>
      {q.isError && <PanelError error={q.error} />}

      <fieldset className="recuadro-ficha">
        <legend>
          {elegido ? `Cartera de ${elegido.codigo} · ${elegido.nombre}` : 'Todos los vendedores'}
        </legend>
        <div className="cifras-cartola" aria-label="Resumen de la cartera">
          <span>
            Clientes listados <b>{filas.length}</b>
            {q.data && <> de {cartera} en cartera</>}
          </span>
          <span>
            En mora <b className="tono-malo-texto">{cuantos('EN_MORA')}</b>
            {Number(vencidoMora) > 0 && <> ({pesos(vencidoMora)} vencido)</>}
          </span>
          {cuantos('VENCIDO_TOLERADO') > 0 && (
            <span>
              Vencidos con tolerancia <b>{cuantos('VENCIDO_TOLERADO')}</b>
            </span>
          )}
          <span>
            Al día <b className="tono-ok-texto">{cuantos('AL_DIA')}</b>
          </span>
          <span>
            Saldo <b>{pesos(suma((c) => c.saldo))}</b>
          </span>
          <span>
            Por vencer <b>{pesos(suma((c) => c.tramos.corriente))}</b>
          </span>
          <span>
            Vencido <b className="tono-malo-texto">{pesos(suma((c) => c.vencido))}</b>
          </span>
          <span>
            A favor <b>{pesos(suma((c) => c.saldoAFavor))}</b>
          </span>
          <span>
            Neto <b>{pesos(suma((c) => c.neto))}</b>
          </span>
        </div>
      </fieldset>

      {!vendedorId && totales.length > 0 && (
        <fieldset className="recuadro-ficha">
          <legend>Por vendedor</legend>
          <table className="grilla compacta grilla-vendedores" aria-label="Totales por vendedor">
            <thead>
              <tr>
                <th>Vendedor</th>
                <th className="al-der">Cartera</th>
                <th className="al-der">Con saldo</th>
                <th className="al-der">En mora</th>
                <th className="al-der">Al día</th>
                <th className="al-der">Saldo</th>
                <th className="al-der">Por vencer</th>
                <th className="al-der">Vencido</th>
                <th className="al-der">+90</th>
                <th className="al-der">A favor</th>
                <th className="al-der">Neto</th>
              </tr>
            </thead>
            <tbody>
              {totales.map((v) => (
                <tr
                  key={v.vendedorId ?? 'sin'}
                  className={v.vendedorId ? 'elegible' : ''}
                  title={v.vendedorId ? 'Ver solo este vendedor' : undefined}
                  onClick={() => v.vendedorId && setVendedorId(v.vendedorId)}
                >
                  <td>
                    <span className="mono">{v.codigo ?? ''}</span> {v.nombre}
                  </td>
                  <td className="al-der">{v.cartera || ''}</td>
                  <td className="al-der">{v.clientes || ''}</td>
                  <td className="al-der">
                    {v.porEstado.EN_MORA ? (
                      <b className="tono-malo-texto">{v.porEstado.EN_MORA}</b>
                    ) : (
                      ''
                    )}
                  </td>
                  <td className="al-der">{v.porEstado.AL_DIA || ''}</td>
                  <td className="al-der">
                    <b>{plata(v.saldo)}</b>
                  </td>
                  <td className="al-der">{plata(v.tramos.corriente)}</td>
                  <td className="al-der tono-malo-texto">{plata(v.vencido)}</td>
                  <td className="al-der tono-malo-texto">{plata(v.tramos['91+'])}</td>
                  <td className="al-der">{plata(v.saldoAFavor)}</td>
                  <td className="al-der">{plata(v.neto)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </fieldset>
      )}

      <fieldset className="recuadro-ficha">
        <legend>Clientes · Enter abre su cartola</legend>
        <Grilla
          etiqueta="Cartola por vendedor"
          columnas={columnas}
          filas={filas}
          clave={(c) => c.clienteEntidadId}
          cargando={q.isLoading}
          vacio={
            vendedorId
              ? 'Ningún cliente de este vendedor tiene saldo con este filtro'
              : 'Ningún cliente tiene saldo con este filtro'
          }
          alAbrir={(c) => alAbrirCartola(c.clienteEntidadId, c.cliente)}
        />
      </fieldset>

      {imprimir && q.data && (
        <ImprimirCartolaVendedor
          titulo={
            elegido ? `Vendedor: ${elegido.codigo} ${elegido.nombre}` : 'Todos los vendedores'
          }
          vendedores={totales}
          clientes={filas}
          detalle={conDocumentos}
          alTerminar={terminar}
        />
      )}
    </div>
  );
}
